import styled, { css } from 'styled-components'
import { links } from '../../../../router/routes'
import { Link } from 'react-router-dom'
import { Memecoin } from '../../../../types/reducerTypes'
import { Box } from '../../../UI/Box'
import { Span } from '../../Typeography'
import Dotdotdot from 'react-dotdotdot'

const LaunchCard = ({
  memecoin,
  bottom,
  progress = 0,
  twentyFourHourPctChange = 0,
  highlighted = false,
}: {
  memecoin: Memecoin
  bottom?: React.ReactNode
  progress?: number
  twentyFourHourPctChange?: number
  highlighted?: boolean
}) => {
  return (
    <Cont highlighted={highlighted}>
      <Link
        to={`${links.memecoins.address(memecoin.poolAddress)}`}
        className="card-link"
      >
        <TokenCont>
          <TokenLogoContainer>
            <TokenLogo
              src={memecoin?.media?.logo?.url ?? '/meme-icon.png'}
              alt="Token Logo"
            />
            <ProgressBar $progress={progress} />
          </TokenLogoContainer>
          <TokenInfoCont>
            <TokenNameCont>
              <TokenName>
                <Dotdotdot clamp={1}>{memecoin.tokenName}</Dotdotdot>
              </TokenName>
              <TokenSymbol>{memecoin.tokenSymbol}</TokenSymbol>
            </TokenNameCont>
            <TwentyFourHourPriceChange
              twentyFourHourPctChange={twentyFourHourPctChange}
            />
          </TokenInfoCont>
        </TokenCont>
        {bottom && (
          <>
            <BottomCont>{bottom}</BottomCont>
          </>
        )}
      </Link>
    </Cont>
  )
}

export default LaunchCard

export function TwentyFourHourPriceChange({ twentyFourHourPctChange = 0 }) {
  return !!twentyFourHourPctChange ? (
    <PriceChange $isPositive={twentyFourHourPctChange > 0} fu aw ac>
      <img
        src={`/img/icons/trend-${twentyFourHourPctChange > 0 ? 'up' : 'down'}.svg`}
        alt="Trend Icon"
      />
      <Span s="L" fw="semibold">
        {Math.abs(twentyFourHourPctChange).toLocaleString()}%
      </Span>
    </PriceChange>
  ) : (
    <></>
  )
}

const PriceChange = styled(Box) <{ $isPositive: boolean }>`
span{ 
color: ${({ $isPositive, theme }) => theme.semantics.zap.function[$isPositive ? 'success' : 'error']};
`

const TokenLogo = styled.img`
  width: 100%;
  min-width: 140px;
  max-width: 400px;
  aspect-ratio: 1;
  display: flex;
  flex-shrink: 0;
  position: relative;
`

const Cont = styled.div<{ highlighted?: boolean }>`
  display: flex;
  min-width: 140px;
  max-width: 400px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: var(--units-8, 8px);

  border: var(--units-1, 1px) solid
    ${({ highlighted, theme }) =>
    highlighted ? theme.colors.brandPrimary : 'transparent'};

  backdrop-filter: blur(40px);

  cursor: pointer;
  transition: border-color 0.2s ease-in-out;

  ${({ highlighted }) =>
    highlighted &&
    css`
      max-width: 404px;
    `}
  .card-link {
    width: 100%;
  }

  overflow: hidden;

  ${TokenLogo} {
    transition: transform 0.4s ease-in-out;
  }
  :hover {
    ${TokenLogo} {
      transform: scale(1.03);
    }
  }

  @media screen and (min-width: ${({ theme }) =>
    theme.layout.medium.indexGrids.maxWidth}px) {
    min-width: 220px;
    max-width: 400px;
  }
`

const TokenCont = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
  align-items: flex-start;
  gap: var(--units-8, 8px);
  align-self: stretch;
`

const TokenLogoContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: var(--units-4, 4px);
`

const ProgressBar = styled.div<{ $progress: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: calc(${({ $progress }) => `${$progress}%`});
  height: 4px;
  background: var(--surface-brand, #ff0);
  border-bottom-left-radius: var(--units-4, 4px);
  border-bottom-right-radius: ${({ $progress }) =>
    $progress === 100 ? 'var(--units-4, 4px)' : '0'};
`

const TokenInfoCont = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--units-2, 2px);
  flex: 1 0 0;
  align-self: stretch;
  line-height: normal;
`

const TokenNameCont = styled.div`
  flex: unset;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  line-height: normal;
`

const TokenName = styled.h3`
  overflow: hidden;
  color: var(--text-primary, #fff);
  text-overflow: ellipsis;
  /* Web/Text/L/Semibold */
  font-family: var(--type-family-space-grotesk, 'Space Grotesk');
  font-size: var(--Text-L-L-size, 16px);
  font-style: normal;
  font-weight: var(--type-weight-Semibold, 600);
  line-height: var(--Text-L-L-line-height, 20px); /* 125% */
`

const TokenSymbol = styled.span`
  color: var(--text-secondary, #b1b1b1);
  /* Web/Text/S/Regular */
  font-family: var(--type-family-space-grotesk, 'Space Grotesk');
  font-size: var(--Text-S-S-size, 12px);
  font-style: normal;
  font-weight: var(--type-weight-Regular, 300);
  line-height: var(--Text-S-S-line-height, 16px); /* 133.333% */
  max-height: var(--Text-S-S-line-height, 16px); /* 133.333% */
`

const Description = styled.div`
  /* Web/Text/L/Regular */
  font-family: var(--type-family-space-grotesk, 'Space Grotesk');
  font-size: var(--Text-L-L-size, 16px);
  font-style: normal;
  font-weight: var(--type-weight-Regular, 300);
  line-height: var(--Text-L-L-line-height, 20px); /* 125% */
`

const Divider = styled.div`
  height: var(--units-1, 1px);
  align-self: stretch;
  background: var(--surface-minimal-primary, rgba(255, 255, 255, 0.04));
`

const BottomCont = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--units-6, 6px);
  align-self: stretch;
`

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--units-4, 4px);
  align-self: stretch;
`

export const RowsLayout = styled.div<{ isTrendingList?: boolean }>`
  display: grid;
  width: 100%;
  gap: 16px var(--Grids-Gutters, 16px);
  padding-top: var(--units-20, 20px);
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));

  @keyframes flashBackground {
    0% {
      background: var(--surface-minimal-primary, rgba(255, 255, 255, 0.04));
    }
    50% {
      background-color: #ffff22;
      color: #000000;
    }
    100% {
      background: var(--surface-minimal-primary, rgba(255, 255, 255, 0.04));
    }
  }

  ${({ isTrendingList }) =>
    isTrendingList &&
    `
    ${Cont}:first-child {
      animation: flashBackground 1s ease-in-out;
    }
  `}

  ${Cont} {
    flex: 1 1 0;
    margin; 0;
  }

  @media screen and (min-width: ${({ theme }) =>
    theme.layout.medium.indexGrids.maxWidth}px) {
    gap: 20px var(--Spacing-Padding-L, 20px);
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  }

  @media screen and (max-width: ${({ theme }) =>
    theme.layout.medium.indexGrids.maxWidth}px) {
    gap: 20px var(--Spacing-Padding-L, 20px);
  }

  @media screen and (max-width: ${({ theme }) =>
    theme.layout.compact.indexGrids.maxWidth}px) {
    gap: 16px var(--Spacing-Padding-L, 16px);
    grid-template-columns: repeat(auto-fill, minmax(140px, 2fr));
  }
`
